import { Sparkles } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { cn } from '../lib/utils.ts';
import { notYetBuiltText } from './not-yet-built.tsx';
import { ChatterColumn } from './chatter.tsx';
import { CardHeadTabs, useCardHeadTabs } from './ui/tab-card.tsx';

/**
 * Seitenleiste einer Detailseite (DESIGN „Rechte Seitenleiste“, Baustein-Katalog „Datensatz-Seitenleiste“; EXPERIENCE §10.4;
 * Tickets 1.204, 1.312): baut aus der Beschreibung des Datensatzes auf jeder Art dieselbe Leiste – oben die Vorschau-Karte
 * (`preview`), dann **eine** Karte: mit Kennzahlen „Kennzahlen“ mit Umschalter „Kennzahlen · Verlauf“ im Kartenkopf
 * ([RUDOLF 19.10.2026]), ohne Kennzahlen nur „Verlauf“ ([RUDOLF 24.10.2026]); darunter der KI-Hinweis (`hint`). Den Verlauf
 * (`ChatterColumn`) setzt nur diese Stelle ein. Die Breite gibt `RecordLayout` vor (`{spacing.record-side}`), unter
 * Desktop-Breite steht sie unter dem Inhalt. `side`/`sideTabs` kommen vom Rahmen (gewählter Teil, Umschalter).
 */
export function RecordSidebar({
  label = 'Seitenleiste',
  preview,
  figures,
  history,
  hint,
  side,
  sideTabs,
}: {
  label?: string;
  preview?: ReactNode;
  figures: ReactNode | null;
  history: { entityType: string; entityId: string } | null;
  hint?: ReactNode;
  /** Gewählter Teil (`kennzahlen` oder `verlauf`). */
  side: string;
  /** Umschalter „Kennzahlen · Verlauf“ (oder `null`, wenn es nur einen Teil gibt). */
  sideTabs: ReactNode | null;
}) {
  const toggle = sideTabs !== null;
  const withFigures = figures !== null;
  if (!preview && !withFigures && !history && !hint) return null;
  return (
    <aside aria-label={label} data-testid="seitenleiste" className="flex min-w-0 flex-col gap-card-gap">
      {preview}
      {withFigures ? (
        <div hidden={toggle && side !== 'kennzahlen'} data-seitenleiste-teil="kennzahlen" className="flex min-w-0 flex-col gap-card-gap">
          {/* Umschalter im Kopf der Karte „Kennzahlen“ statt ihres Titels ([RUDOLF 19.10.2026]). */}
          <CardHeadTabs title="Kennzahlen" tabs={toggle && side === 'kennzahlen' ? sideTabs : null}>
            <RecordSidebarSection title="Kennzahlen" testId="kennzahlen">
              {figures}
            </RecordSidebarSection>
          </CardHeadTabs>
        </div>
      ) : null}
      {history ? (
        <div hidden={toggle ? side !== 'verlauf' : undefined} data-seitenleiste-teil="verlauf" data-testid="verlauf" className="min-w-0">
          {/* … und im Kopf der Verlauf-Karte, solange sie gezeigt wird. */}
          <CardHeadTabs tabs={toggle && side === 'verlauf' ? sideTabs : null}>
            <ChatterColumn entityType={history.entityType} entityId={history.entityId} />
          </CardHeadTabs>
        </div>
      ) : null}
      {hint}
    </aside>
  );
}

/**
 * Abschnitt der Seitenleiste: Titel, optionale Meta-Zeile (z. B. Anzahl, Stand) und Inhalt in einer Karte. Stellt der
 * Rahmen Reiter für diesen Titel bereit (`CardHeadTabs`, z. B. „Kennzahlen · Verlauf“), stehen sie im Kopf statt des
 * Titels ([RUDOLF 19.10.2026]: Reiter in der Karte); der Titel bleibt als Name für Hilfsmittel.
 */
export function RecordSidebarSection({
  title,
  meta,
  action,
  children,
  testId,
}: {
  title: string;
  meta?: ReactNode;
  /** Kleiner Zweitknopf rechts neben dem Titel (z. B. „Alle“). */
  action?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  const id = useId();
  const headTabs = useCardHeadTabs(title);
  return (
    <section aria-labelledby={id} data-testid={testId} className="card-box min-w-0 break-words">
      <div className="mb-space-3 flex items-start justify-between gap-space-3">
        <div className="min-w-0">
          <h3 id={id} className={headTabs ? 'sr-only' : 'text-gauge leading-snug font-medium text-ink'}>
            {title}
          </h3>
          {headTabs}
          {meta ? <p className="type-meta mt-space-1 text-ink-3">{meta}</p> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * KI-Hinweis in der Seitenleiste (AD-16: nur Vorschlag). Zarte blaue Fläche mit Kante, Funken-Symbol und der Pflichtsatz
 * „Nur Vorschläge – die KI bucht nie selbst.“; `actions` ist der Platz für „Ansehen“ / „Verwerfen“.
 */
export function RecordAiHint({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      data-testid="ki-hinweis"
      className="card-box min-w-0 break-words !bg-[linear-gradient(180deg,var(--card)_0%,var(--s3-blue-50)_100%)] !shadow-card ring-1 ring-[var(--s3-blue-300)] ring-inset"
    >
      <p className="type-meta flex items-center gap-space-2 text-link">
        <Sparkles aria-hidden size={14} strokeWidth={1.5} />
        KI-Hinweis
      </p>
      <h3 id={id} className="mt-space-2 text-gauge leading-snug font-medium text-ink">
        {title}
      </h3>
      <div className="mt-space-2 text-row text-ink-2">{children}</div>
      {actions ? <div className="mt-space-3 flex flex-wrap gap-space-2">{actions}</div> : null}
      <p className="type-meta mt-space-3 text-ink-3">Nur Vorschläge – die KI bucht nie selbst.</p>
    </section>
  );
}

/** Ruhige Kennzahl-Zeilen der Seitenleiste (Name links, Wert rechts 18 px, Vergleich als Satz darunter). */
export function RecordMetricRows({ rows }: { rows: readonly { label: string; value: ReactNode; note?: ReactNode; story?: string | null }[] }) {
  return (
    <dl className="m-0 flex flex-col">
      {rows.map((m) => {
        const later = m.story !== undefined;
        const hint = later ? notYetBuiltText(m.story ?? undefined) : null;
        const cls = 'grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-space-3 gap-y-space-1 border-t border-line py-space-2 first:border-t-0 first:pt-0 last:pb-0';
        // Ausgegraut wie `NotYetBuilt`, der Hinweis aber im `dd` (in einem `dl` sind nur `dt`/`dd` gültig).
        return (
          <div
            key={m.label}
            className={cn(cls, later ? 'cursor-not-allowed text-ink-3 opacity-60' : '')}
            {...(later ? { 'data-testid': 'noch-nicht-gebaut', 'data-story': m.story ?? undefined, 'aria-disabled': true, title: hint ?? undefined } : {})}
          >
            <dt className="text-row text-ink-2">{m.label}</dt>
            <dd className="relative text-right text-gauge font-medium tabular-nums text-ink [overflow-wrap:anywhere]">
              {m.value}
              {hint ? <span className="sr-only">{` (${hint})`}</span> : null}
            </dd>
            {m.note ? <dd className="type-meta col-span-2 text-ink-3">{m.note}</dd> : null}
          </div>
        );
      })}
    </dl>
  );
}

/**
 * Verlauf eines Datensatzes ohne Seitenleisten-Karte (Redesign R2, Story 26.15): für Detailseiten mit eigenem Aufbau
 * (Auftrag, Kunde, Angebot), die rechts nur den Verlauf zeigen. Damit setzt auch dort nur dieser Baustein `ChatterColumn` ein.
 */
export function RecordHistory({ entityType, entityId }: { entityType: string; entityId: string }) {
  return (
    <div data-testid="verlauf" className="min-w-0">
      <ChatterColumn entityType={entityType} entityId={entityId} />
    </div>
  );
}
